import { useCallback, useEffect, useState } from "react";
import EditableContent, { type EditableContentProps } from "./EditableContent";

/**
 * Optimized input component that prevents cursor jumping during typing.
 *
 * This component solves the cursor jumping issue by:
 * 1. Using local state to control the input value during typing
 * 2. Preventing external value updates while the user is actively typing
 * 3. Syncing the final value on blur
 *
 * Use this instead of InvisibleInput when dealing with frequently updating
 * controlled inputs that cause cursor position issues.
 */
const OptimizedInvisibleInput = ({
  value: propValue,
  onChange,
  onBlur,
  onFocus,
  ...props
}: EditableContentProps) => {
  const [localValue, setLocalValue] = useState(propValue);
  const [isTyping, setIsTyping] = useState(false);

  // Update local value when prop value changes (external updates) only if not currently typing
  useEffect(() => {
    if (!isTyping) {
      setLocalValue(propValue);
    }
  }, [propValue, isTyping]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      setIsTyping(true);

      // Update parent immediately but with flexible event structure
      if (onChange) {
        onChange({
          target: { value: newValue },
        } as React.ChangeEvent<HTMLTextAreaElement>);
      }
    },
    [onChange],
  );

  const handleBlur = useCallback(() => {
    setIsTyping(false);
    onBlur?.({} as React.FocusEvent<HTMLTextAreaElement>);
  }, [onBlur]);

  const handleFocus = useCallback(() => {
    setIsTyping(true);
    onFocus?.({} as React.FocusEvent<HTMLTextAreaElement>);
  }, [onFocus]);

  return (
    <EditableContent
      {...props}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
    />
  );
};

export default OptimizedInvisibleInput;
